import AddIcon from "@mui/icons-material/Add";
import { Box, Container, Dialog, Fab } from "@mui/material";
import Alert from "@mui/material/Alert";
import { TableCellType } from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import SortableTable from "components/SortableTable";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useListTitle from "hooks/useListTitle";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  ListResponseOfRatingSetting,
  RatingSetting,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import QuestionRatingCreateForm from "./QuestionRatingCreateForm";

interface RatingSettingWithId extends RatingSetting {
  id: string;
}

const QuestionRatings = () => {
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { questionService } = useGlobalContext();
  const [createOpen, createOpenActions] = useBoolean(false);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const {
    isError,
    data,
    error,
    isFetching,
  } = useQuery<ListResponseOfRatingSetting>("questionRatings", () =>
    questionService.getRatingSettings(null)
  );

  useListTitle("questionRatingsTitle");

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const onQuestionCreated = () => {
    queryClient.invalidateQueries("questionRatings");
    createOpenActions.setFalse();

    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "questionRatingCreated" }))
    );
  };

  const questionRatings = (data?.data ?? []) as RatingSettingWithId[];

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "questionRatingNameLabel" }),
    },
    {
      accessor: "minValueText",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "questionRatingMinLabel" }),
    },
    {
      accessor: "maxValueText",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "questionRatingMaxLabel" }),
    },
    {
      accessor: "optionCount",
      align: "center",
      width: 0,
      type: "text",
      label: t({ id: "questionRatingCountLabel" }),
    },
  ];

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Fab
          aria-label="add"
          color="primary"
          onClick={createOpenActions.setTrue}
          data-cy="addButton"
        >
          <AddIcon />
        </Fab>
      </Box>

      <SortableTable
        {...{
          rows: questionRatings,
          loading: isFetching,

          dense: true,
          title: t({ id: "questionRatingsTitle" }),
          cells,
        }}
      />

      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <QuestionRatingCreateForm
          onQuestionRatingCreated={onQuestionCreated}
          onCancel={createOpenActions.setFalse}
        />
      </Dialog>
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default QuestionRatings;
